import { CAD_SUBPATH, GBP_SUBPATH } from "../helpers/market";

export default class LocalizationForm extends HTMLElement {
  constructor() {
    super();

    if (!this.form) return;

    // dialog only instantiates for one instance of custom element per page,
    // but we track submit in all cases so that form submission updates
    // browser storage with user market preference
    this.bindFormSubmit();

    if (
      this.dataset.checkMarket !== "true" ||
      !this.select ||
      !this.dialog ||
      !this.closeButton ||
      !this.consentButton ||
      !this.rejectButton
    )
      return;

    // has user already specified a preferred market via consent dialog or localization form?
    if (this.hasPreferredMarket) {
      // if so, and current subpath matches preferred market, delete dialog
      if (this.preferredMarket === this.currentMarketSubpath) {
        this.dialog?.remove();
      } else {
        // otherwise automatically redirect to preferred market rather than show consent dialog
        this.redirectToNewMarket(this.preferredMarket);
      }
      // bail from remaining script
      return;
    }

    this.checkMarket();
  }

  get storageKey() {
    return "verso_preferred_market";
  }

  get preferredMarket() {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${this.storageKey}=`))
      ?.split("=")[1];
  }

  get hasPreferredMarket() {
    return (
      this.preferredMarket === "GB" ||
      this.preferredMarket === "CA" ||
      this.preferredMarket === "US"
    );
  }

  get currentMarketSubpath() {
    const currentUrl = new URL(window.location);
    if (currentUrl.pathname.startsWith(GBP_SUBPATH)) return "GB";
    if (currentUrl.pathname.startsWith(CAD_SUBPATH)) return "CA";
    return "US";
  }

  get form() {
    return this.querySelector("form");
  }

  get select() {
    return this.querySelector("select");
  }

  get dialog() {
    const dialogId = this.dataset.dialogId;

    if (!dialogId) return null;

    const el = document.getElementById(dialogId);

    return el instanceof HTMLDialogElement ? el : null;
  }

  get trapContainer() {
    return this.dialog?.querySelector("[data-ref='trapContainer']");
  }

  get closeButton() {
    return this.dialog?.querySelector("button[data-ref='close']");
  }

  get consentButton() {
    return this.dialog?.querySelector("button[data-ref='consent']");
  }

  get rejectButton() {
    return this.dialog?.querySelector("button[data-ref='reject']");
  }

  get currentMarketNode() {
    return this.dialog?.querySelector("[data-ref='currentMarket']");
  }

  get suggestedMarketNode() {
    return this.dialog?.querySelector("[data-ref='suggestedMarket']");
  }

  checkMarket = async () => {
    const marketSuggestions = await fetch(
      window.Shopify.routes.root +
        "browsing_context_suggestions.json" +
        "?country[enabled]=true" +
        `&country[exclude]=${window.Shopify.country}`,
    )
      .then((response) => response.json())
      .catch((error) => {
        console.error(error);
      });

    const suggestedCountry =
      marketSuggestions.suggestions[0]?.parts.country?.handle;
    const suggestedMarket = this.getMarketForCountry(suggestedCountry);

    // if Shopify suggests a different market for the customer based on their location,
    // prompt user to consent to being redirected to right market site
    if (suggestedMarket && this.currentMarketSubpath !== suggestedMarket) {
      this.setMarketNodeText(this.currentMarketSubpath, suggestedMarket);
      this.consentButton.addEventListener("click", () =>
        this.redirectToNewMarket(suggestedMarket),
      );
      this.rejectButton.addEventListener("click", () => this.dialog.close());
      this.closeButton.addEventListener("click", () => this.dialog.close());
      this.dialog.addEventListener("close", () =>
        this.proceedWithCurrentMarket(this.currentMarketSubpath),
      );
      this.dialog.showModal();
    }
  };

  getMarketForCountry(country) {
    if (!country) return undefined;
    // treat all non-US/non-CA locations as UK/ROW market
    return country !== "US" && country !== "CA" ? "GB" : country;
  }

  getMarketLabel(handle) {
    const l10nKey = handle.toLowerCase();

    switch (l10nKey) {
      case "ca":
        return "Canada (CAD $)";
      case "us":
        return "United States (USD $)";
      case "gb":
      case "row":
        return "UK/ROW (GBP £)";
      default:
        return handle;
    }
  }

  setMarketNodeText(currentMarket, suggestedMarket) {
    if (currentMarket && this.currentMarketNode) {
      this.currentMarketNode.innerText = this.getMarketLabel(currentMarket);
    }

    if (suggestedMarket && this.suggestedMarketNode) {
      this.suggestedMarketNode.innerText = this.getMarketLabel(suggestedMarket);
    }
  }

  redirectToNewMarket = (preferredMarket) => {
    this.select.value = preferredMarket;
    this.form.requestSubmit();
  };

  proceedWithCurrentMarket = (market) => {
    this.setPreferredMarket(market);
  };

  bindFormSubmit = () => {
    this.form.addEventListener("submit", (event) => {
      const formData = new FormData(event.target);
      const market = formData.get("country_code");
      this.setPreferredMarket(market);
    });
  };

  // indicate in browser storage that user has expressed preferred market
  setPreferredMarket = (market) => {
    // let's be extra certain that an invalid value is never set in the cookie
    if (market !== "GB" && market !== "CA" && market !== "US") return;

    const expires = 60 * 60 * 24 * 14; // 14 days
    document.cookie = `${this.storageKey}=${market};path=/;max-age=${expires}`;
  };
}
